import { BIN_QR_ID_LENGTH, binIdKey } from "@/labels/storageBinJobs";

/**
 * Finds the bin a scan points to (FIX-319). Used by every bin scan: the
 * MUNBYN hand scanner and the camera, in harvesting, part labels and inventory.
 *
 * 1. Id: a new label (`BIN:` + 16 id characters), a full bin id or a
 *    `/bin/<id>` link selects exactly that bin. Codes are not tried then.
 * 2. Code: an old label carries only the bin code (`A01-B1-L1-S1-B01`, or a
 *    shorter one from FIX-318). Codes are unique only within one storage
 *    location, so a code found in two locations returns `choose` and the
 *    screen asks which one. It never picks one silently.
 */
export type BinScanResult<T> =
  | { kind: "bin"; bin: T }
  | { kind: "choose"; bins: T[] }
  | { kind: "none" };

interface ScannableBin {
  id: string;
  address: string;
}

// Separator after BIN is optional and may be any non-alphanumeric character,
// so a scanner on another keyboard layout (":" typed as "Ö") still matches.
const BIN_QR_RE = new RegExp(`BIN[^0-9A-Z]?([0-9A-F]{${BIN_QR_ID_LENGTH}})(?![0-9A-F])`, "i");
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

const result = <T,>(matches: T[]): BinScanResult<T> =>
  matches.length === 1
    ? { kind: "bin", bin: matches[0] }
    : matches.length > 1
      ? { kind: "choose", bins: matches }
      : { kind: "none" };

export function resolveScannedBin<T extends ScannableBin>(raw: string, bins: T[]): BinScanResult<T> {
  const text = raw.trim();
  if (!text) return { kind: "none" };

  // 1. Id
  const uuid = text.match(UUID_RE)?.[0].toLowerCase();
  if (uuid) return result(bins.filter((b) => b.id.toLowerCase() === uuid));
  const key = text.match(BIN_QR_RE)?.[1].toUpperCase();
  if (key) return result(bins.filter((b) => binIdKey(b.id) === key));

  // 2. Code: the whole scan, its last path segment, then each word in it.
  const upper = text.replace(/\s+/g, "").toUpperCase();
  const candidates = [
    upper,
    upper.split("/").filter(Boolean).pop() ?? "",
    ...text.toUpperCase().split(/[^0-9A-Z-]+/),
  ].filter(Boolean);
  for (const c of candidates) {
    const matches = bins.filter((b) => b.address.toUpperCase() === c);
    if (matches.length) return result(matches);
  }
  return { kind: "none" };
}
